import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Checkbox, Button, message } from 'antd';
import { useMutation } from 'react-query';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { sanitizeHTML } from '@/utils/sanitize';
import TermsApiService from '@/modules/terms/services/TermsApiService';
// Quill's stylesheet so rendered content matches exactly how it looks in the editor.
import '@/modules/terms/quill.snow.css';

const sanitizer = (value) => sanitizeHTML(value);

// Box is the scroller; the inner .ql-editor provides Quill's padding/line-height/margins.
// Height scales with the viewport so tall screens show more text instead of wasting space.
const boxStyle = {
  maxHeight: 'calc(100vh - 340px)',
  minHeight: 240,
  overflowY: 'auto',
  border: '1px solid #D9D9D9',
  borderRadius: 6,
  background: '#FAFAFA',
};

const TermsModal = ({ terms, onAccepted, onReject, refetch }) => {
  const { t } = useTranslation('admin');
  const [checked, setChecked] = useState(false);

  const { mutate, isLoading } = useMutation(() => TermsApiService.accept(terms.version), {
    onSuccess: () => onAccepted(),
    onError: (e) => {
      const status = e?.response?.status || e?.response?.data?.statusCode;
      if (status === 409) {
        // The active version changed underneath us: reload and ask again.
        message.warning(t('terms.outdated'));
        setChecked(false);
        refetch();
      } else {
        message.error(t('terms.acceptError'));
      }
    },
  });

  const effective = terms?.effectiveDate ? moment(terms.effectiveDate).format('L') : '';

  return (
    <Modal
      visible
      centered
      closable={false}
      maskClosable={false}
      keyboard={false}
      footer={null}
      width={760}
      title={null}
    >
      {terms?.isUpdate && (
        <span
          style={{
            display: 'inline-block',
            background: '#E6F4FB',
            color: '#0B8AD1',
            fontSize: 13,
            fontWeight: 500,
            padding: '2px 10px',
            borderRadius: 12,
            marginBottom: 10,
          }}
        >
          {t('terms.newVersionBadge', { version: terms?.version })}
        </span>
      )}
      <div style={{ fontSize: 24, fontWeight: 500, marginBottom: 4 }}>
        {terms?.isUpdate ? t('terms.updatedTitle') : t('terms.title')}
      </div>
      <p style={{ fontSize: 15, color: '#595959', marginBottom: 16 }}>
        {terms?.isUpdate ? t('terms.updatedSubtitle') : t('terms.subtitle')}
      </p>

      <div tabIndex={0} aria-label={t('terms.ariaContent')} style={boxStyle} className="ql-snow">
        <div
          className="ql-editor"
          style={{ height: 'auto', overflow: 'visible' }}
          dangerouslySetInnerHTML={{ __html: sanitizer(terms?.contentHtml) }}
        />
      </div>

      <div style={{ margin: '12px 0', fontSize: 13, color: '#595959' }}>
        {t('terms.versionMeta', { version: terms?.version, date: effective })}
      </div>

      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
        <Checkbox checked={checked} onChange={(e) => setChecked(e.target.checked)} />
        <span>
          <span style={{ color: '#ff4d4f' }}>* </span>
          {terms?.acceptanceText || t('terms.acceptLabel')}
        </span>
      </label>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
        <Button type="link" danger onClick={onReject} style={{ minHeight: 44 }}>
          {t('terms.reject')}
        </Button>
        <Button
          type="primary"
          disabled={!checked}
          loading={isLoading}
          onClick={() => mutate()}
          style={{ minHeight: 44 }}
        >
          {t('terms.accept')}
        </Button>
      </div>
    </Modal>
  );
};

TermsModal.propTypes = {
  terms: PropTypes.object.isRequired,
  onAccepted: PropTypes.func.isRequired,
  onReject: PropTypes.func.isRequired,
  refetch: PropTypes.func.isRequired,
};

export default TermsModal;
